<template>
  <b-modal
    ref="printLabelsModal"
    size="xxl"
    :title="$t('component.printLabelsModal.printLabels')"
    centered
    modal-class="print-labels-modal"
    hide-footer
    @hide="onHide"
  >
    <b-row>
      <b-col
        lg="4"
        xl="3"
        class="pr-2 pl-4"
      >
        <div class="scroll-area p-3">
          <b-row>
            <b-col>
              <div>
                <span class="ap-title ap-title__main">{{ $t('component.printLabelsModal.printQuestion') }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <div v-if="templates.length">
                  <div class="d-flex align-items-center">
                    <div class="template-truncate">
                      <span class="main template-text">{{ selectedTemplateId ? templateForm.title : $t('component.printLabelsModal.newTemplate') }}</span>
                    </div>
                    <b-button
                      id="pop-print-template-modal"
                      class="btn__icon btn__icon--primary  px-2"
                    >
                      <i
                        class="far fa-chevron-down"
                        aria-hidden="true"
                      />
                    </b-button>
                  </div>
                  <b-popover
                    target="pop-print-template-modal"
                    placement="bottom"
                    triggers="click blur"
                  >
                    <div class="ap-popover">
                      <div class="ap-popover__header">
                        {{ $t('component.printLabelsModal.loadTemplate') }}
                      </div>
                      <ul>
                        <li
                          v-for="template in templates"
                          :key="template.id"
                        >
                          <b-button @click="selectTemplate(template)">
                            {{ template.title }}
                          </b-button>
                        </li>
                      </ul>
                    </div>
                  </b-popover>
                </div>
                <div v-if="!templates.length">
                  {{ $t('component.printLabelsModal.newTemplate') }}
                </div>
                <div>
                  <b-button
                    v-tooltip="$t('component.printLabelsModal.new')"
                    class="btn__icon btn__icon--primary"
                    @click="deleteFormContent"
                  >
                    <i
                      class="fal fa-plus"
                      aria-hidden="true"
                    />
                  </b-button>
                  <b-button
                    v-if="templates.length"
                    v-tooltip="$t('component.printLabelsModal.seeTemplates')"
                    class="btn__icon btn__icon--primary"
                    style="margin-right:0 !important"
                    @click="showTemplatesListModal"
                  >
                    <i
                      class="far fa-list"
                      aria-hidden="true"
                    />
                  </b-button>
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <div class="form-label">
                <label>{{ $t('component.printLabelsModal.caseType') }} *</label>
              </div>
              <vt-combo-select
                v-model="templateForm.template.caseType"
                v-validate="'required'"
                track-by="id"
                :allow-empty="false"
                :options="caseTypes"
                label="descriptionInLaboratory"
                :placeholder="$t('component.printLabelsModal.caseType')"
              />
            </b-col>
          </b-row>
          <b-row class="mt-2">
            <b-col cols="12">
              <div class="switch-btn mt-1 p-0 d-flex align-items-center">
                <input
                  id="toggleBlockModal"
                  v-model="templateForm.template.rangeLabel"
                  type="checkbox"
                  class="offscreen"
                  aria-hidden="true"
                >
                <label
                  for="toggleBlockModal"
                  class="switch mb-0"
                />
                <span class="ml-2 dark">{{ $t('component.printLabelsModal.useRank') }}</span>
              </div>
            </b-col>
          </b-row>
          <div class="rank-block d-flex align-items-center">
            <div
              v-if="!templateForm.template.rangeLabel"
              class="mt-2 d-flex w-100 align-items-center"
            >
              <div class="w-50">
                <span class="next-title dark">{{ $t('component.printLabelsModal.printTheNext') }}</span>
              </div>
              <div class="w-50">
                <vt-input
                  v-model="templateForm.template.numberOfNextLabels"
                  type="number"
                  class="form-control-sm"
                />
              </div>
            </div>
            <div
              v-else
              class="mt-2 d-flex w-100 align-items-center"
            >
              <div class="w-50 pr-1">
                <div class="form-label">
                  <label>{{ $t('component.printLabelsModal.since') }}</label>
                </div>
                <vt-input
                  v-model="templateForm.template.minValue"
                  class="form-control-sm"
                  type="number"
                  data-vv-as="minValue"
                />
              </div>
              <div class="w-50 pl-1">
                <div class="form-label">
                  <label>{{ $t('component.printLabelsModal.to') }}</label>
                </div>
                <vt-input
                  v-model="templateForm.template.maxValue"
                  class="form-control-sm"
                  type="number"
                  :disabled="!templateForm.template.minValue || templateForm.template.minValue <= 0"
                />
              </div>
            </div>
          </div>
          <b-row v-if="templateForm.template.rangeLabel">
            <b-col>
              <alert-banner
                :show="templateForm.template.rangeLabel"
                variant="warning"
                class="mt-3 mb-0"
                close
                :text="$tc('component.printLabelsModal.alertBanner')"
              />
            </b-col>
          </b-row>
          <b-row class="label-types mt-4">
            <b-col cols="12">
              <span class="ap-title ap-title__section">{{ $t('component.printLabelsModal.labelsAndQuantities') }}</span>
            </b-col>
            <b-col class="mt-1">
              <b-row class="my-1 column-header">
                <b-col cols="6">
                  {{ $t('component.printLabelsModal.labelType') }}
                </b-col>
                <b-col class="pr-1">
                  {{ $t('component.printLabelsModal.number') }}
                </b-col>
                <b-col class="pl-1">
                  {{ $t('component.printLabelsModal.copies') }}
                </b-col>
              </b-row>
              <b-row
                v-for="(entityCopy, index) in templateForm.template.entityCopiesRequestList"
                :key="index"
                class="my-1"
              >
                <b-col cols="6">
                  <b-form-checkbox
                    v-model="entityCopy.checked"
                    @change="entitySelected(entityCopy)"
                  >
                    <div
                      v-if="entityCopy.entity === 'CASE'"
                      class="entity d-flex"
                    >
                      <div>
                        <i
                          class="main fal fa-chart-network"
                          aria-hidden="true"
                        />
                      </div>
                      <span class="ml-1 dark">{{ $t('component.printLabelsModal.cases') }}</span>
                    </div>
                    <div
                      v-if="entityCopy.entity === 'SAMPLE'"
                      class="entity d-flex"
                    >
                      <div>
                        <i
                          class="main fal fa-prescription-bottle"
                          aria-hidden="true"
                        />
                      </div>
                      <span class="ml-1 dark">{{ $t('component.printLabelsModal.samples') }}</span>
                    </div>
                    <div
                      v-if="entityCopy.entity === 'PORTION'"
                      class="entity d-flex"
                    >
                      <div>
                        <i
                          class="main icon-block"
                          aria-hidden="true"
                        />
                      </div>
                      <span class="ml-1 dark">{{ $t('component.printLabelsModal.portions') }}</span>
                    </div>
                    <div
                      v-if="entityCopy.entity === 'SUBPORTION'"
                      class="entity d-flex"
                    >
                      <div>
                        <i
                          class="main icon-porta"
                          aria-hidden="true"
                        />
                      </div>
                      <span class="ml-1 dark">{{ $t('component.printLabelsModal.subportions') }}</span>
                    </div>
                  </b-form-checkbox>
                </b-col>
                <b-col class="pr-1">
                  <vt-input
                    v-model.number="entityCopy.numEntities"
                    type="number"
                    class="form-control-sm"
                  />
                </b-col>
                <b-col class="pl-1">
                  <vt-input
                    v-model.number="entityCopy.numCopies"
                    type="number"
                    class="form-control-sm"
                  />
                </b-col>
                <b-col
                  v-if="entityCopy.entity === 'SUBPORTION' && entityCopy.checked"
                  cols="12"
                >
                  <div class="horizontal-divider my-2" />
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="dark">{{ $t('component.printLabelsModal.slidesFromBlocks') }}<i
                      v-tooltip="$t('component.printLabelsModal.slidesFromBlocksTooltip')"
                      class="main ml-2 fal fa-info-circle"
                      aria-hidden="true"
                    /></span>
                    <div class="switch-btn mt-1 p-0 d-flex align-items-center">
                      <input
                        id="toggleSubportion"
                        v-model="templateForm.template.hasVirtualPortion"
                        type="checkbox"
                        class="offscreen"
                        aria-hidden="true"
                      >
                      <label
                        for="toggleSubportion"
                        class="switch mb-0"
                      />
                    </div>
                  </div>
                  <div class="horizontal-divider my-2" />
                </b-col>
                <!-- TODO: Section for magazine, modify v-if="false" with v-if="entityCopy.entity === 'PORTION' && entityCopy.checked" -->
                <!-- TODO: Front development required to save with print template -->
                <b-col
                  v-if="false"
                  cols="12"
                >
                  <div class="horizontal-divider my-2" />
                  <b-row>
                    <b-col class="d-flex align-items-center">
                      <span class="dark">{{ $t('component.printLabelsModal.magazine') }} *</span>
                    </b-col>
                    <b-col cols="8">
                      <vt-combo-select
                        v-model="magazineSelected"
                        track-by="reportModelEnum"
                        :custom-label="(value) => `${value.printer} - ${value.magazine}`"
                        :option-loader="getAllConfiguratedMagazines"
                      />
                    </b-col>
                  </b-row>
                  <div class="horizontal-divider my-2" />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <!-- TODO: Section hidden until development -->
          <b-row
            v-if="false"
            class="label-models mt-4"
          >
            <b-col cols="12">
              <span class="ap-title ap-title__section">{{ $t('component.printLabelsModal.labelsModels') }}</span>
            </b-col>
            <b-col>
              <b-row>
                <b-col class="d-flex align-items-center">
                  <div class="entity d-flex align-items-center">
                    <div>
                      <i
                        class="main fal fa-prescription-bottle"
                        aria-hidden="true"
                      />
                    </div>
                    <span class="ml-1 dark">{{ $t('component.printLabelsModal.samples') }}</span>
                  </div>
                </b-col>
                <b-col class="pl-0">
                  <div class="d-flex justify-content-between align-items-center model-selector">
                    <div class="model-truncate">
                      <span class="main">{{ $t('component.printLabelsModal.selectModel') }}</span>
                    </div>
                    <b-button
                      id="pop-print-model"
                      class="btn__icon btn__icon--primary  pl-2 pr-0"
                      style="margin-right: 0 !important"
                    >
                      <i
                        class="far fa-chevron-down"
                        aria-hidden="true"
                      />
                    </b-button>
                    <b-popover
                      target="pop-print-model"
                      triggers="click blur"
                    >
                      <div class="ap-popover">
                        <div class="ap-popover__header">
                          {{ $t('component.printLabelsModal.selectModel') }}
                        </div>
                        <ul>
                          <!-- TODO: Mock data, pending develop -->
                          <li>
                            <b-button>
                              Modelo 1
                            </b-button>
                          </li>
                          <li>
                            <b-button>
                              Modelo 1
                            </b-button>
                          </li>
                          <li>
                            <b-button>
                              Modelo 1
                            </b-button>
                          </li>
                        </ul>
                      </div>
                    </b-popover>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>
        <div class="footer-area py-3">
          <b-row class="px-3">
            <b-col>
              <b-button
                size="sm"
                block
                class="btn__link"
                @click="showSavePrintTemplateModal"
              >
                {{ $t('component.printLabelsModal.saveTemplate') }}
              </b-button>
            </b-col>
            <b-col>
              <vt-loading-button
                class="btn--primary btn-sm btn-block"
                :params="{ promise: generateLabels }"
              >
                {{ $t('component.printLabelsModal.generate') }}
              </vt-loading-button>
            </b-col>
          </b-row>
        </div>
      </b-col>
      <b-col
        v-if="!labels.length"
        class="empty-state-section pl-0"
      >
        <div class="empty-state-layout p-5 mt-4 col-xl-7">
          <div class="d-flex align-items-center justify-content-center">
            <i
              class="fal fa-ticket-alt"
              aria-hidden="true"
            />
            <div class="circle" />
          </div>
          <div class="my-5">
            <h3>{{ $t('component.printLabelsModal.emptyTitle') }}</h3>
          </div>
          <div class="px-3 mt-4">
            <b-row>
              <b-col>
                <span>{{ $t('component.printLabelsModal.emptyHelpOne') }}</span>
              </b-col>
              <b-col>
                <span>{{ $t('component.printLabelsModal.emptyHelpTwo') }}</span>
              </b-col>
              <b-col>
                <span>{{ $t('component.printLabelsModal.emptyHelpThree') }}</span>
              </b-col>
            </b-row>
          </div>
          <div>
            <b-row class="mt-5 text-center">
              <b-col
                cols="12"
                class="mt-5"
              >
                <span
                  class="main"
                  style="font-size: 15px"
                >{{ $t('global.emptyState.needHelp') }}</span>
              </b-col>
              <b-col cols="12">
                <b-button class="btn__icon btn__icon--primary main">
                  {{ $t('global.emptyState.userManual') }}
                </b-button>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
      <b-col
        v-if="labels.length"
        class="grid-section pl-0"
      >
        <div class="p-3">
          <b-row>
            <b-col>
              <div class="labels-card d-flex py-2 align-items-center">
                <div class="block d-flex flex-column text-center">
                  <small>{{ $t('component.printLabelsModal.labelsGenerated') }}</small>
                  <span>{{ labels.length }}</span>
                </div>
                <div class="line" />
                <div class="block d-flex flex-column text-center">
                  <small>{{ $t('component.printLabelsModal.totalToPrint') }}</small>
                  <span>{{ totalLabelsToPrint }}</span>
                </div>
              </div>
            </b-col>
            <b-col>
              <div class="labels-card d-flex py-2 align-items-center">
                <div class="block d-flex flex-column text-center">
                  <small>{{ $t('component.printLabelsModal.labelsPrinted') }}</small>
                  <span>{{ getPrintedLabels.length }}</span>
                </div>
                <div class="line" />
                <div class="block d-flex flex-column text-center">
                  <small>{{ $t('component.printLabelsModal.labelsUsed') }}</small>
                  <span>{{ getUsedLabels.length }}</span>
                </div>
              </div>
            </b-col>
          </b-row>
          <b-row class="mt-3">
            <b-col>
              <vt-grid
                pagination
                class="print-labels-grid"
                :grid-options="gridOptions"
                :row-data="labels"
                :column-defs="columnDefs"
              />
            </b-col>
          </b-row>
          <div class="grid-footer">
            <b-row class="px-0">
              <b-col class="d-flex justify-content-end">
                <div
                  v-if="labels.some((label) => label.reportModel === ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL)"
                  class="d-flex align-items-center mr-2"
                >
                  <span
                    style="white-space:nowrap"
                    class="type-selector-text dark mr-2"
                  >{{ $t('component.printLabelsModal.magazine') }} *</span>
                  <vt-combo-select
                    v-model="magazineSelected"
                    track-by="reportModelEnum"
                    :custom-label="(value) => `${value.printer} - ${value.magazine}`"
                    style="width:200px"
                    :option-loader="getAllConfiguratedMagazines"
                  />
                </div>
                <b-button
                  size="sm"
                  class="btn__outline mr-2"
                  @click="printLabels(getUnprintedLabels)"
                >
                  {{ $t('component.printLabelsModal.printOnlyNoPrinted') }} ({{ getUnprintedLabels.length }})
                </b-button>
                <b-button
                  size="sm"
                  class="btn--primary"
                  @click="printLabels()"
                >
                  {{ $t('component.printLabelsModal.printLabelsButton') }}
                </b-button>
              </b-col>
            </b-row>
          </div>
        </div>
      </b-col>
    </b-row>
    <save-print-template-modal
      ref="savePrintTemplateModal"
      :selected-template-id="selectedTemplateId"
      @updateTemplates="onTemplatesUpdated($event)"
    />
    <templates-list-modal
      ref="templatesListModal"
      @selectTemplate="selectTemplate($event)"
      @onTemplatesDeleted="onTemplatesDeleted"
    />
  </b-modal>
</template>

<script>
import VtGrid from '@/components/VtGrid/VtGrid'
import gridSideBarConfig from '@/grid/gridSideBarConfig'
import i18n from '@/lang/i18n'
import SavePrintTemplateModal from '@/components/modals/printLabelsModal/SavePrintTemplateModal'
import TemplatesListModal from '@/components/modals/printLabelsModal/TemplatesListModal'
import batchPrintingDashboardServices, { COREENTITY_PRINT_LABELS_VALUES, ENTITY_LABELS } from '@/api/resources/common/batchPrintingDashboardServices'
import CommonCaseTypeServices from '@/api/resources/common/commonCaseTypeServices'
import { cloneDeep, sumBy } from 'lodash'
import magazineInformationServices from '@/api/resources/common/magazineInformationServices'
import AlertBanner from '@/components/common/AlertBanner/alert-banner.vue'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'

export default {
    name: 'PrintLabelsModal',
    components: {
        VtGrid,
        SavePrintTemplateModal,
        TemplatesListModal,
        AlertBanner
    },
    props: {
        sample: Object,
        isCaseValidated: Boolean
    },
    data() {
        return {
            ENTITY_LABELS: ENTITY_LABELS,
            magazineSelected: null,
            originalGeneratedTemplate: null,
            selected: null,
            selectedTemplateId: null,
            templates: [],
            caseTypes: [],
            templateForm: {
                description: '',
                template: {
                    caseType: null,
                    entityCopiesRequestList: [
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.CASE,
                            reportModel: ENTITY_LABELS.REGISTRY_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        },
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.SAMPLE,
                            reportModel: ENTITY_LABELS.SAMPLE_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        },
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.PORTION,
                            reportModel: ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        },
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.SUBPORTION,
                            reportModel: ENTITY_LABELS.SUBPORTION_SLIDE_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        }
                    ],
                    hasVirtualPortion: false,
                    reportModelsList: [],
                    maxValue: 1,
                    minValue: 1,
                    numberOfNextLabels: 0,
                    rangeLabel: false,
                    reportConfigId: []
                },
                title: '',
                toBeUsedByDefault: false
            },
            switchRankModal: null,
            selectedRows: [],
            columnDefs: [
                { type: 'customCheckbox' },
                {
                    type: 'text',
                    headerName: i18n.t('component.printLabelsModal.labelName'),
                    colId: 'label',
                    field: 'label',
                    width: 150,
                    minWidth: 140,
                    suppressSizeToFit: true
                },
                {
                    type: 'text',
                    headerName: i18n.t('component.printLabelsModal.printDate'),
                    colId: 'printingDate',
                    field: 'printingDate',
                    width: 150,
                    minWidth: 180,
                    suppressSizeToFit: true
                },
                {
                    type: 'text',
                    headerName: i18n.t('component.printLabelsModal.printUser'),
                    colId: 'userapp.fullName',
                    field: 'userapp.fullName',
                    width: 200,
                    minWidth: 100,
                    suppressSizeToFit: true
                },
                {
                    type: 'checkIconRenderer',
                    headerName: i18n.t('component.printLabelsModal.labelUsed'),
                    colId: 'entityId',
                    field: 'entityId',
                    flex: 1,
                    width: 180,
                    minWidth: 180,
                    suppressSizeToFit: true
                }
            ],
            labels: []
        }
    },
    computed: {
        totalLabelsToPrint() {
            if (this.labels.length) {
                return sumBy(this.labels, 'numCopies')
            }
            return 0
        },
        getUnprintedLabels() {
            if (this.labels.length) {
                return this.labels.filter((label) => !label.printingDate)
            }
            return []
        },
        getPrintedLabels() {
            if (this.labels.length) {
                return this.labels.filter((label) => label.printingDate)
            }
            return []
        },
        getUsedLabels() {
            if (this.labels.length) {
                return this.labels.filter((label) => label.entityId)
            }
            return []
        },
        labId() {
            return this.$store.getters.labId
        },
        gridOptions() {
            return {
                sideBar: gridSideBarConfig,
                rowModelType: AgGridRowModelType.CLIENT_SIDE,
                getContextMenuItems: this.getContextMenuItems,
                onGridReady: this.onGridReady,
                onSelectionChanged: this.onSelectionChanged
            }
        }
    },
    methods: {
        deleteFormContent() {
            this.templateForm = {
                description: this.templateForm.description,
                template: {
                    caseType: null,
                    entityCopiesRequestList: [
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.CASE,
                            reportModel: ENTITY_LABELS.REGISTRY_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        },
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.SAMPLE,
                            reportModel: ENTITY_LABELS.SAMPLE_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        },
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.PORTION,
                            reportModel: ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        },
                        {
                            entity: COREENTITY_PRINT_LABELS_VALUES.SUBPORTION,
                            reportModel: ENTITY_LABELS.SUBPORTION_SLIDE_LABEL,
                            numCopies: 1,
                            numEntities: 1
                        }
                    ],
                    hasVirtualPortion: false,
                    reportModelsList: [],
                    maxValue: 0,
                    minValue: 0,
                    numberOfNextLabels: 0,
                    rangeLabel: false,
                    reportConfigId: []
                },
                title: this.templateForm.title,
                toBeUsedByDefault: this.templateForm.toBeUsedByDefault
            }
            this.selectedTemplateId = null
            this.selected = null
        },
        async onTemplatesDeleted(templateIdsDeleted) {
            const currentTemplateDeleted = templateIdsDeleted.includes(this.selectedTemplateId)
            if (!currentTemplateDeleted) {
                await this.loadTemplates(this.templates.find((template) => template.id === this.selectedTemplateId))
            } else {
                await this.loadTemplates()
            }
            this.$refs.templatesListModal.assignTemplateRowData(this.templates)
            this.$emit('reloadTemplates')
        },
        async show(templateSelected) {
            await this.loadTemplates(templateSelected)
            await this.loadCaseTypes()
            this.$refs.printLabelsModal.show()
        },
        async validate() {
            return this.$validator.validate()
        },
        mapLabelTemplate(labelTemplate) {
            return {
                caseTypeId: labelTemplate.caseType && labelTemplate.caseType.id,
                entityCopiesRequestList: labelTemplate.entityCopiesRequestList,
                hasVirtualPortion: labelTemplate.hasVirtualPortion,
                maxValue: labelTemplate.maxValue,
                minValue: labelTemplate.minValue,
                numberOfNextLabels: labelTemplate.numberOfNextLabels,
                rangeLabel: labelTemplate.rangeLabel,
                reportConfigId: labelTemplate.reportConfigId,
                reportModelsList: labelTemplate.reportModelsList
            }
        },
        async generateLabels() {
            const validation = await this.validate()
            if (!validation) {
                this.showErrorModalGlobal(this.$t('global.incompleteForm'))
                return
            }
            if (this.templateForm.template.rangeLabel && this.templateForm.template.minValue <= 0) {
                this.showErrorModalGlobal(this.$t('component.printLabelsModal.invalidMinRangeValue'))
                return
            }
            const batchPrintingTemplateConfigurationDto = this.mapLabelTemplate(this.templateForm.template)
            const [err, response] = await this.toCall(batchPrintingDashboardServices.generateLabels(this.labId, batchPrintingTemplateConfigurationDto))
            if (err) {
                if (err.response && err.response.data) {
                    this.showErrorModalGlobal(err.response.data.message)
                } else {
                    this.showErrorModalGlobal()
                }
                return
            }
            if (response.length === 0) {
                this.showErrorModalGlobal(this.$t('component.printLabelsModal.noLabels'))
            }
            this.originalGeneratedTemplate = cloneDeep(this.templateForm)
            this.labels = response
        },
        selectTemplate(template) {
            this.selectedTemplateId = template.id
            this.templateForm.title = template.title
            this.templateForm.description = template.description
            this.templateForm.toBeUsedByDefault = template.toBeUsedByDefault
            this.templateForm.template.caseType = template.batchPrinting.caseType
            this.templateForm.template.hasVirtualPortion = template.batchPrinting.hasVirtualPortion
            this.templateForm.template.maxValue = template.batchPrinting.maxValue
            this.templateForm.template.minValue = template.batchPrinting.minValue
            this.templateForm.template.numberOfNextLabels = template.batchPrinting.numberOfNextLabels
            this.templateForm.template.rangeLabel = template.batchPrinting.rangeLabel
            this.templateForm.template.reportModelsList = template.batchPrinting.reportModelsList
            this.templateForm.template.entityCopiesRequestList = template.batchPrinting.entityCopiesRequestList
            this.templateForm.template.entityCopiesRequestList.forEach((entity) => {
                if (entity.entity === COREENTITY_PRINT_LABELS_VALUES.CASE) {
                    entity.reportModel = ENTITY_LABELS.REGISTRY_LABEL
                }
                if (entity.entity === COREENTITY_PRINT_LABELS_VALUES.SAMPLE) {
                    entity.reportModel = ENTITY_LABELS.SAMPLE_LABEL
                }
                if (entity.entity === COREENTITY_PRINT_LABELS_VALUES.PORTION) {
                    entity.reportModel = ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL
                }
                if (entity.entity === COREENTITY_PRINT_LABELS_VALUES.SUBPORTION) {
                    entity.reportModel = ENTITY_LABELS.SUBPORTION_SLIDE_LABEL
                }
            })
            template.batchPrinting.reportModelsList.forEach((label) => {
                this.templateForm.template.entityCopiesRequestList.find((entity) => entity.reportModel === label).checked = true
            })
            this.$root.$emit('bv::hide::popover')
        },
        onHide() {
            this.$_cleanComponentData()
        },
        onTemplatesUpdated(templateSelected) {
            this.loadTemplates(templateSelected)
            this.$emit('reloadTemplates')
        },
        async printLabels(rows = null) {
            if (this.templateForm.template.reportModelsList.includes(ENTITY_LABELS.CASSETTE_PARAFFIN_BLOCK_LABEL) && !this.magazineSelected) {
                this.showErrorModalGlobal(this.$t('print.modals.selectMagazineToContinue'))
                return
            }
            const selectedRows = rows || this.gridApi.getSelectedRows()
            if (selectedRows.length) {
                this.$emit('printLabels', { templateForm: this.templateForm, selectedRows: selectedRows, magazineSelected: this.magazineSelected })
            } else if (!rows) {
                this.$emit('printLabels', { templateForm: this.originalGeneratedTemplate, selectedRows: selectedRows, magazineSelected: this.magazineSelected })
            }
        },
        async loadTemplates(templateSelected = null) {
            const [err, response] = await this.toCall(batchPrintingDashboardServices.getTemplatesByLaboratory(this.labId))
            if (err) {
                this.showErrorModalGlobal()
                return
            }
            this.templates = response
            if (this.templates.length) {
                const defaultTemplate = this.templates.find((template) => template.toBeUsedByDefault)
                if (templateSelected) {
                    this.selectTemplate(templateSelected)
                } else if (defaultTemplate) {
                    this.selectTemplate(defaultTemplate)
                } else {
                    this.selectTemplate(this.templates[0])
                }
            } else {
                const currentCaseTypes = this.caseTypes
                this.$_cleanComponentData()
                this.caseTypes = currentCaseTypes
            }
        },
        async loadCaseTypes() {
            const [err, response] = await this.toCall(CommonCaseTypeServices.getActiveCaseTypesByLaboratory(this.labId))
            if (err) {
                this.showErrorModalGlobal()
            }
            this.caseTypes = response
        },
        hide() {
            this.$refs.printLabelsModal.hide()
        },
        async showSavePrintTemplateModal() {
            const validation = await this.validate()
            if (!validation) {
                this.showErrorModalGlobal(this.$t('global.incompleteForm'))
                return
            }
            this.$refs.savePrintTemplateModal.show(this.templateForm)
        },
        showTemplatesListModal() {
            this.$refs.templatesListModal.show(this.templates)
        },
        entitySelected(entity) {
            this.$nextTick(() => {
                if (entity.checked) {
                    this.templateForm.template.reportModelsList.push(entity.reportModel)
                } else {
                    this.templateForm.template.reportModelsList = this.templateForm.template.reportModelsList.filter((label) => label !== entity.reportModel)
                }
                this.$forceUpdate()
            })
        },
        getContextMenuItems(params) {
            return [
                {
                    name: this.$t('component.printLabelsModal.printLabel'),
                    action: () => this.printLabels([params?.node.data])
                },
                'separator',
                {
                    name: this.$t('component.printLabelsModal.printAll'),
                    action: () => this.printLabels()
                },
                {
                    name: this.$t('component.printLabelsModal.printOnlyNoPrinted'),
                    action: () => this.printLabels(this.getUnprintedLabels)
                }
            ]
        },
        onSelectionChanged({ api }) {
            this.selectedRows = api.getSelectedRows()
        },
        onGridReady({ api }) {
            this.gridApi = api
        },
        async getAllConfiguratedMagazines() {
            return magazineInformationServices.getAllConfiguratedMagazines(this.labId)
        }
    }
}
</script>

<style lang="scss">
    .print-labels-modal .empty-state-section {
        text-align: center;
    }
    .print-labels-modal .empty-state-section span {
        font-size: font-size(very-small) !important;
    }
    .print-labels-modal .empty-state-section h3 {
        font-size: font-size(very-big);
    }
    .print-labels-modal .empty-state-section i {
        font-size: 40px;
        color: #FFF;
    }
    .print-labels-modal .empty-state-section span {
        font-size: 16px;
        line-height: 20px;
        color: vt-color(dark-text);
    }
    .print-labels-modal .empty-state-section .circle {
        background: vt-color(light-primary);
        width: 100px;
        height: 100px;
        border-radius: 50%;
    }
    .print-labels-modal .empty-state-section {
        display: flex;
        align-items: center;
        flex-direction: column;
    }
    .print-labels-modal .empty-state-section > div i {
        position: absolute;
    }
    .print-labels-modal .empty-state-section > div {
        position: relative;
    }
    .print-labels-modal .grid-footer {
        margin-top: 20px;
        bottom: 0;
        width: 100%;
    }
    .print-labels-modal .print-labels-grid {
        height: calc(100vh - 390px);
    }
    .print-labels-modal .print-labels-grid .ag-root-wrapper-body {
        padding: 0;
    }
    .print-labels-modal .labels-card {
        border: 1px solid vt-color(grid-general-border);
        box-shadow: 0 1px 3px 0 #cacaca !important;
    }
    .print-labels-modal .labels-card .block {
        flex: 1;
        line-height: 20px;
    }
    .print-labels-modal .labels-card .line {
        width: 1px;
        background: vt-color(grid-general-border);
        height: 30px;
        margin: 5px;
    }
    .print-labels-modal .labels-card small {
        text-transform: uppercase;
    }
    .print-labels-modal .labels-card span {
        color: vt-color(dark-text);
        font-size: font-size(very-big);
    }
    .print-labels-modal .template-truncate {
        max-width:200px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .print-labels-modal .model-truncate {
        max-width:150px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    .print-labels-modal .model-selector span {
        font-size: font-size(very-small);
    }
    .print-labels-modal .scroll-area {
        height: calc(100vh - 300px);
        overflow-y: auto;
        overflow-x: hidden;
    }
    .print-labels-modal .label-types .entity div,
    .print-labels-modal .label-models .entity div {
        width: 25px;
        text-align: center;
    }
    .print-labels-modal .label-types .entity,
    .print-labels-modal .label-models .entity {
        font-size: font-size(very-small);
    }
    .print-labels-modal .label-types .title,
    .print-labels-modal .label-models .title {
        font-size: font-size(normal);
    }
    .print-labels-modal .label-types .column-header {
        font-size: font-size(small);
    }
    .print-labels-modal .next-title {
        font-size: font-size(very-small);
    }
    .print-labels-modal .rank-block {
        height: 50px;
    }
    .print-labels-modal .title {
        color: vt-color(dark-text);
        font-size: font-size(very-normal);
    }
    .print-labels-modal .modal-body {
        padding: 0;
        border-radius: .3rem;
    }
    .print-labels-modal .switch-label {
        font-size: font-size(very-small);
        color: vt-color(dark-text);
    }
    .print-labels-modal .switch {
        position: relative;
        display: inline-block;
        width: 30px;
        height: 16px;
        background-color: rgba(0, 0, 0, 0.25);
        border-radius: 20px;
        transition: all 0.3s;
        cursor: pointer;
    }
    .print-labels-modal .switch::after {
        content: "";
        position: absolute;
        width: 14px;
        height: 14px;
        border-radius: 14px;
        background-color: white;
        top: 1px;
        left: 1px;
        transition: all 0.3s;
    }
    .print-labels-modal input[type="checkbox"]:checked + .switch::after {
        transform: translateX(14px);
    }
    .print-labels-modal input[type="checkbox"]:checked + .switch {
        background-color: vt-color(primary);
    }
    .print-labels-modal .offscreen {
        position: absolute;
        left: -9999px;
    }
    .print-labels-modal .grid-section,
    .print-labels-modal .empty-state-section {
        border-left: 1px solid vt-color(grid-general-border);
    }
    @media only screen and (max-width: 1366px) and (min-width: 1360px) {
        .print-labels-modal .scroll-area {
            height: calc(100vh - 160px);
        }
        .print-labels-modal .print-labels-grid {
            height: calc(100vh - 240px);
        }
    }
</style>
